import {ExclamationIcon} from '@primer/octicons-react'
import {MergeabilityFailureIcon} from '@github-ui/custom-icons/MergeabilityFailureIcon'
import {UpdateBranchButton} from '@github-ui/mergebox/components/sections/conflicts/UpdateBranchButton'
import {ForYouItem} from '../shared/ForYouItem'
import {type MergeabilityData, useMergeabilityData} from './use-mergeability-data'
import {OverviewMergeControl} from './shared/OverviewMergeControl'
import {Banner} from '@primer/react'
import {useState} from 'react'
import {announce} from '@github-ui/aria-live'

export type BranchUpdateRequirement = 'required' | 'optional'

const FOR_YOU_ANALYTICS_OVERRIDES = {
  selectMergeMethod: {
    eventType: 'overview.for_you_item_action',
    target: 'OVERVIEW_FOR_YOU_ITEM',
    payload: {item_name: 'update-branch', action_name: 'select_merge_commit_method'},
  },
  selectRebaseMethod: {
    eventType: 'overview.for_you_item_action',
    target: 'OVERVIEW_FOR_YOU_ITEM',
    payload: {item_name: 'update-branch', action_name: 'select_rebase_method'},
  },
  updateBranch: {
    eventType: 'overview.for_you_item_action',
    target: 'OVERVIEW_FOR_YOU_ITEM',
    payload: {item_name: 'update-branch', action_name: 'update_branch'},
  },
}

export function getBranchUpdateRequirement(data: MergeabilityData | null): BranchUpdateRequirement | null {
  if (
    !data ||
    data.isClosedOrMerged ||
    // `viewerCanUpdateBranch` (PullRequest#async_branch_is_updatable_by?) requires the branch to
    // actually be behind base (PullRequest#behind_base?), a plain git ahead/behind comparison
    // independent of mergeStateStatus. It rules out an already up-to-date PR, but a behind branch
    // can still have conflicts, an advisory workspace, or be stacked/pending — the shouldRender and
    // sectionStatus checks below exclude those so this item doesn't preempt the ones that handle them.
    !data.mergeBoxPullRequest.viewerCanUpdateBranch ||
    !data.conflictsSection?.shouldRender ||
    !(data.conflictsSection?.sectionStatus === 'OUT_OF_DATE' || data.conflictsSection?.sectionStatus === 'NO_CONFLICTS')
  ) {
    return null
  }

  return data.requiresBranchUpdate ? 'required' : 'optional'
}

export function UpdateBranch() {
  const data = useMergeabilityData()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  if (!data) return null

  const updateRequirement = getBranchUpdateRequirement(data)

  if (!updateRequirement) return null

  const isRequired = updateRequirement === 'required'

  return (
    <ForYouItem
      itemId="update-branch"
      visual={
        isRequired
          ? {kind: 'icon', icon: MergeabilityFailureIcon, tone: 'danger', glyphSize: 12}
          : {kind: 'icon', icon: ExclamationIcon, tone: 'attention', glyphSize: 12}
      }
      title={isRequired ? 'Required to update' : 'Out of date'}
      subText={
        isRequired
          ? 'Update your branch with the base branch before merging.'
          : 'Update your branch with the base branch when convenient.'
      }
      actions={[
        {
          id: 'update-branch',
          content: (
            <UpdateBranchButton
              analyticsOverrides={FOR_YOU_ANALYTICS_OVERRIDES}
              headRefOid={data.mergeBoxPullRequest.headRefOid}
              onError={error => {
                setErrorMessage(error)
                // The visible Banner below is not a live region, so screen-reader users focused on
                // the update button would otherwise hear nothing when the mutation fails.
                if (error) announce(error, {assertive: true})
              }}
              size="small"
              viewerUpdateMethods={data.mergeBoxPullRequest.viewerUpdateMethods}
            />
          ),
        },
        {
          id: 'merge',
          content: (
            <OverviewMergeControl
              data={data}
              itemName="update-branch"
              fallbackAction={isRequired ? 'view-status' : 'merge'}
            />
          ),
        },
      ]}
      footer={
        errorMessage && (
          <Banner className="tmp-m-3" variant="critical" title="Error" hideTitle>
            {errorMessage}
          </Banner>
        )
      }
    />
  )
}
